/**
 * Validated mood/anxiety screeners used in patient intake: PHQ-9 (depression)
 * and GAD-7 (generalized anxiety). Question wording is the standard public-
 * domain instrument text; do not paraphrase — clinicians rely on the exact
 * items and the 0-3 frequency scale for scoring and comparability.
 *
 * Shared between web and mobile so the questions, scale, and scoring bands can
 * never drift between platforms.
 */

// 0-3 frequency scale, shared by both instruments.
export const SCREENER_FREQUENCY_OPTIONS: ReadonlyArray<readonly [number, string]> = [
  [0, 'Not at all'],
  [1, 'Several days'],
  [2, 'More than half the days'],
  [3, 'Nearly every day'],
]

export const PHQ9_PROMPT =
  'Over the last 2 weeks, how often have you been bothered by any of the following problems?'

export const PHQ9_QUESTIONS: readonly string[] = [
  'Little interest or pleasure in doing things',
  'Feeling down, depressed, or hopeless',
  'Trouble falling or staying asleep, or sleeping too much',
  'Feeling tired or having little energy',
  'Poor appetite or overeating',
  'Feeling bad about yourself — or that you are a failure or have let yourself or your family down',
  'Trouble concentrating on things, such as reading the newspaper or watching television',
  'Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual',
  'Thoughts that you would be better off dead, or of hurting yourself in some way',
]

export const GAD7_PROMPT =
  'Over the last 2 weeks, how often have you been bothered by the following problems?'

export const GAD7_QUESTIONS: readonly string[] = [
  'Feeling nervous, anxious, or on edge',
  'Not being able to stop or control worrying',
  'Worrying too much about different things',
  'Trouble relaxing',
  "Being so restless that it's hard to sit still",
  'Becoming easily annoyed or irritable',
  'Feeling afraid, as if something awful might happen',
]

/** Sum non-null responses. Returns null if nothing has been answered yet. */
export function screenerScore(responses: (number | null | undefined)[] | null | undefined): number | null {
  if (!responses || responses.length === 0) return null
  let any = false
  let total = 0
  for (const r of responses) {
    if (r != null) {
      any = true
      total += r
    }
  }
  return any ? total : null
}

export type ScreenerSeverity =
  | 'minimal'
  | 'mild'
  | 'moderate'
  | 'moderately severe'
  | 'severe'

/** PHQ-9 severity band (0-27). */
export function phq9Severity(score: number): ScreenerSeverity {
  if (score >= 20) return 'severe'
  if (score >= 15) return 'moderately severe'
  if (score >= 10) return 'moderate'
  if (score >= 5) return 'mild'
  return 'minimal'
}

/** GAD-7 severity band (0-21). */
export function gad7Severity(score: number): ScreenerSeverity {
  if (score >= 15) return 'severe'
  if (score >= 10) return 'moderate'
  if (score >= 5) return 'mild'
  return 'minimal'
}
